import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { FolderPlus, Upload, Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { Button } from "@/shared/components/ui/Button";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { Toast } from "@/shared/components/feedback/Toast";
import { Breadcrumb } from "@/features/files/components/Breadcrumb";
import { FolderCard } from "@/features/files/components/FolderCard";
import { FileCard } from "@/features/files/components/FileCard";
import { CreateFolderModal } from "@/features/files/components/CreateFolderModal";
import { FileUploadModal } from "@/features/files/components/FileUploadModal";
import { FolderSettingsModal } from "@/features/files/components/FolderSettingsModal";
import { useStorageQuota, bytesToGB } from "@/features/files/hooks/useStorageQuota";
import {
  fetchFolderContents,
  getBreadcrumb,
  deleteFolder,
  deleteFile,
  updateFile,
  getFileViewUrl,
} from "@/features/files/services/sharepointService";
import type { SharepointFolder, SharepointFile, BreadcrumbItem, StorageQuota } from "@/features/files/types/sharepoint";
import { showAppLinkNotice } from "@/shared/utils/appLinks";

const OFFICER_TYPES = ["officer", "admin"];

// 403 = outside the user's company, 404 = deleted or never existed.
// Either way a shared link can't be opened, so show the same message.
const isUnavailableError = (err: unknown) =>
  axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 404);

// ── Storage bar ───────────────────────────────────────────────────────────────

const StorageBar = ({ quota }: { quota: StorageQuota }) => {
  const usedGB = bytesToGB(quota.usedStorage).toFixed(2);
  const maxGB = bytesToGB(quota.maxStorage).toFixed(2);
  const clampedPct = Math.min(quota.usagePercent, 100);
  const isWarning = quota.usagePercent >= 80 && quota.usagePercent <= 110;
  const isOver = quota.usagePercent > 110;

  const barColor = isOver
    ? "bg-red-500"
    : isWarning
    ? "bg-amber-400"
    : "bg-indigo-500";

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{usedGB} GB / {maxGB} GB used</span>
        <span
          className={
            isOver ? "text-red-600 font-semibold" :
            isWarning ? "text-amber-600 font-semibold" :
            "text-gray-500"
          }
        >
          {quota.usagePercent.toFixed(1)}%
          {isOver && " — Storage full"}
        </span>
      </div>
      <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${clampedPct}%` }}
        />
      </div>
    </div>
  );
};

export const SharepointPage = () => {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userData } = useAuth();
  const { t } = useTranslation();

  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");
  const currentFolderId = folderId ?? null;
  const { quota, quotaLoading, refreshQuota } = useStorageQuota();

  const [folders, setFolders] = useState<SharepointFolder[]>([]);
  const [files, setFiles] = useState<SharepointFile[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [folderUnavailable, setFolderUnavailable] = useState(false);
  const [fileError, setFileError] = useState("");
  const clearFileError = useCallback(() => setFileError(""), []);

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [settingsFolder, setSettingsFolder] = useState<SharepointFolder | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "folder" | "file"; id: string } | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [renameFile, setRenameFile] = useState<SharepointFile | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameExt, setRenameExt] = useState("");
  const [renameError, setRenameError] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setFolderUnavailable(false);
    try {
      const [contentsData, breadcrumbData] = await Promise.all([
        fetchFolderContents(currentFolderId),
        getBreadcrumb(currentFolderId),
      ]);
      setFolders(
        [...contentsData.folders].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
        ),
      );
      setFiles(contentsData.files);
      setBreadcrumb(breadcrumbData);
    } catch (err) {
      console.error("Failed to load sharepoint data:", err);
      if (isUnavailableError(err)) {
        setFolders([]);
        setFiles([]);
        setBreadcrumb([{ id: null, name: "Files" }]);
        setFolderUnavailable(true);
        if (currentFolderId) {
          // A folder link the user can't open: explain, then return to where
          // they came from (chat, feed, ...) — or the Files root when the link
          // was the first page they opened.
          showAppLinkNotice(t("sharepoint.folder_unavailable"));
          if (location.key !== "default") navigate(-1);
          else navigate("/files", { replace: true });
        }
      }
    } finally {
      setLoading(false);
    }
    // location.key is read once per load, not a reason to reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Deep link: /files/:folderId?file=<id> (from "Copy link" on a file card)
  // opens the file once, then strips the param so refresh doesn't re-open it.
  useEffect(() => {
    const fileParam = searchParams.get("file");
    if (!fileParam) return;
    setSearchParams({}, { replace: true });
    getFileViewUrl(fileParam)
      .then((url) => {
        // window.open is usually popup-blocked outside a click handler;
        // fall back to same-tab navigation so the link always works.
        const win = window.open(url, "_blank");
        if (!win) window.location.assign(url);
      })
      .catch((err) => {
        console.error("Failed to open linked file:", err);
        if (isUnavailableError(err)) setFileError(t("sharepoint.file_unavailable"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleNavigate = (targetFolderId: string | null) => {
    if (targetFolderId) {
      navigate(`/files/${targetFolderId}`);
    } else {
      navigate("/files");
    }
  };

  const handleOpenFolder = (id: string) => {
    navigate(`/files/${id}`);
  };

  const handleViewFile = async (fileId: string) => {
    try {
      const url = await getFileViewUrl(fileId);
      window.open(url, "_blank");
    } catch (err) {
      console.error("Failed to get file URL:", err);
      if (isUnavailableError(err)) setFileError(t("sharepoint.file_unavailable"));
    }
  };

  const handleRenameOpen = (file: SharepointFile) => {
    const lastDot = file.display_name.lastIndexOf(".");
    const baseName = lastDot > 0 ? file.display_name.slice(0, lastDot) : file.display_name;
    const ext = lastDot > 0 ? file.display_name.slice(lastDot) : "";
    setRenameFile(file);
    setRenameValue(baseName);
    setRenameExt(ext);
    setRenameError("");
  };

  const handleRenameConfirm = async () => {
    if (!renameFile) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameError(t("sharepoint.folder_name_required"));
      return;
    }
    setRenameLoading(true);
    setRenameError("");
    try {
      const updated = await updateFile(renameFile.id, { display_name: trimmed + renameExt });
      setFiles((prev) => prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)));
      setRenameFile(null);
    } catch (err) {
      console.error("Rename file failed:", err);
      setRenameError(t("sharepoint.update_failed"));
    } finally {
      setRenameLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleteError("");
    try {
      if (deleteConfirm.type === "folder") {
        const contents = await fetchFolderContents(deleteConfirm.id);
        if (contents.folders.length > 0 || contents.files.length > 0) {
          setDeleteError(t("sharepoint.folder_not_empty"));
          return;
        }
        await deleteFolder(deleteConfirm.id);
      } else {
        await deleteFile(deleteConfirm.id);
      }
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      console.error("Delete failed:", err);
      // Surface the backend's reason (e.g. 409 "file is used in N announcements")
      const apiMsg = (err as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      setDeleteError(apiMsg || t("sharepoint.delete_failed"));
    }
  };

  const isEmpty = folders.length === 0 && files.length === 0;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t("sharepoint.title")}</h1>
          {isOfficer && !folderUnavailable && (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowCreateFolder(true)}
              >
                <FolderPlus className="w-4 h-4 mr-1.5" />
                {t("sharepoint.new_folder")}
              </Button>
              {currentFolderId && quota?.canUpload && (
                <Button size="sm" onClick={() => setShowUploadFile(true)}>
                  <Upload className="w-4 h-4 mr-1.5" />
                  {t("sharepoint.upload")}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Storage quota bar */}
        {!quotaLoading && quota && (
          <StorageBar quota={quota} />
        )}

        <Breadcrumb items={breadcrumb} onNavigate={handleNavigate} />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
        </div>
      ) : folderUnavailable ? (
        <div className="flex flex-col items-center">
          <EmptyState
            icon={<Lock className="w-10 h-10" />}
            title={t("sharepoint.unavailable_title")}
            description={t("sharepoint.unavailable_hint")}
          />
          <Button variant="secondary" size="sm" onClick={() => navigate("/files")}>
            {t("sharepoint.back_to_files")}
          </Button>
        </div>
      ) : isEmpty ? (
        <EmptyState
          title={currentFolderId ? t("sharepoint.empty_folder") : t("sharepoint.no_folders")}
          description={
            isOfficer
              ? currentFolderId
                ? t("sharepoint.officer_folder_empty_hint")
                : t("sharepoint.officer_empty_hint")
              : t("sharepoint.trainee_empty_hint")
          }
        />
      ) : (
        <div className="space-y-6">
          {/* Folders grid */}
          {folders.length > 0 && (
            <div>
              {files.length > 0 && (
                <h2 className="text-sm font-medium text-gray-500 mb-3">{t("sharepoint.folders")}</h2>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {folders.map((folder) => (
                  <FolderCard
                    key={folder.id}
                    folder={folder}
                    isOfficer={isOfficer}
                    onOpen={handleOpenFolder}
                    onDelete={(id: string) => setDeleteConfirm({ type: "folder", id })}
                    onSettings={isOfficer ? (f: SharepointFolder) => setSettingsFolder(f) : undefined}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Files grid */}
          {files.length > 0 && (
            <div>
              {folders.length > 0 && (
                <h2 className="text-sm font-medium text-gray-500 mb-3">{t("sharepoint.files")}</h2>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {files.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    isOfficer={isOfficer}
                    onView={handleViewFile}
                    onDelete={(id: string) => setDeleteConfirm({ type: "file", id })}
                    onRename={isOfficer ? handleRenameOpen : undefined}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {fileError && (
        <Toast message={fileError} type="error" duration={5000} onClose={clearFileError} />
      )}

      {/* Create folder modal */}
      <CreateFolderModal
        isOpen={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        parentId={currentFolderId}
        onCreated={loadData}
      />

      {/* Folder settings modal */}
      {settingsFolder && (
        <FolderSettingsModal
          isOpen={!!settingsFolder}
          onClose={() => setSettingsFolder(null)}
          folder={settingsFolder}
          onUpdated={(updated: SharepointFolder) => {
            setFolders((prev) =>
              prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)),
            );
          }}
        />
      )}

      {/* Upload file modal */}
      {currentFolderId && (
        <FileUploadModal
          isOpen={showUploadFile}
          onClose={() => setShowUploadFile(false)}
          folderId={currentFolderId}
          onUploaded={() => { loadData(); refreshQuota(); }}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => { setDeleteConfirm(null); setDeleteError(""); }} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 mx-4 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("sharepoint.delete_type", { type: deleteConfirm.type })}
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              {deleteConfirm.type === "folder"
                ? t("sharepoint.delete_confirm_folder")
                : t("sharepoint.delete_confirm_file")}
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              {t("sharepoint.delete_reference_warning")}
            </p>
            {deleteError && (
              <p className="text-sm text-red-600 mb-4">{deleteError}</p>
            )}
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => { setDeleteConfirm(null); setDeleteError(""); }} className="flex-1">
                {t("sharepoint.cancel")}
              </Button>
              <Button variant="danger" onClick={handleDeleteConfirm} className="flex-1">
                {t("sharepoint.delete")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rename file modal */}
      {renameFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRenameFile(null)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 mx-4 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("sharepoint.rename_file")}
            </h3>
            <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 mb-3">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleRenameConfirm(); }}
                autoFocus
                className="flex-1 px-3 py-2 text-sm outline-none min-w-0"
              />
              {renameExt && (
                <span className="px-3 py-2 text-sm text-gray-400 bg-gray-50 border-l border-gray-300 shrink-0 select-none">
                  {renameExt}
                </span>
              )}
            </div>
            {renameError && (
              <p className="text-sm text-red-600 mb-3">{renameError}</p>
            )}
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setRenameFile(null)} className="flex-1">
                {t("sharepoint.cancel")}
              </Button>
              <Button onClick={handleRenameConfirm} loading={renameLoading} className="flex-1">
                {t("sharepoint.save")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
